import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const GalleryContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 2rem 4rem;
  background: var(--bg-secondary);
`;

const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.8s ease;
`;

const PageTitle = styled.h1`
  font-size: clamp(3rem, 6vw, 5rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;

  span { color: #F5B800; }
`;

const PageSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
`;

const ImagesSection = styled.section`
  margin-top: 0;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  color: var(--text-primary);
  margin-bottom: 3rem;
  text-align: center;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;

  &:after {
    content: '';
    display: block;
    width: 100px;
    height: 4px;
    background: #F5B800;
    margin: 1rem auto 0;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageCard = styled.div`
  background: var(--card-bg);
  padding: 1.5rem;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  cursor: pointer;
  border-top: 4px solid transparent;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
    border-top-color: #F5B800;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  width: 100%;
  height: 350px;
  background: #f0f0f0;

  @media (max-width: 768px) {
    height: 300px;
  }

  &:after {
    content: '🔍 VIEW';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    font-size: 1.5rem;
    font-weight: 800;
    background: rgba(245, 184, 0, 0.95);
    color: var(--text-primary);
    padding: 1.5rem 3rem;
    border-radius: 4px;
    transition: all 0.3s ease;
    font-family: 'Space Grotesk', sans-serif;
    letter-spacing: 2px;
    z-index: 2;
  }

  ${ImageCard}:hover &:after {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: all 0.4s ease;

  ${ImageCard}:hover & {
    transform: scale(1.05);
  }
`;

const ImageCaption = styled.p`
  text-align: center;
  color: var(--text-secondary);
  font-size: 1.05rem;
  line-height: 1.7;
  font-weight: 500;
`;

const Modal = styled.div`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(14, 17, 20, 0.98);
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 2rem;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  border-radius: 4px;
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.8);
  animation: ${fadeIn} 0.4s ease;
  border: 4px solid #F5B800;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: #F5B800;
  border: none;
  font-size: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(245, 184, 0, 0.4);
  color: var(--text-primary);
  font-weight: 900;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 15px 40px rgba(245, 184, 0, 0.6);
    background: #2EC4B6;
  }
`;

const Gallery = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  const openModal = (imageSrc) => {
    setSelectedImage(imageSrc);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage('');
  };

  return (
    <GalleryContainer>
      <Container>
        <PageHeader>
          <PageTitle>Project <span>Gallery</span></PageTitle>
          <PageSubtitle>Professional Structured Wiring & Cable Management</PageSubtitle>
        </PageHeader>

        <ImagesSection>
          <SectionTitle>Featured Projects</SectionTitle>
          <ImagesGrid>
            <ImageCard onClick={() => openModal('/pictures/ChatGPT Image Jan 10, 2026, 12_36_19 PM.png')}>
              <ImageWrapper>
                <Image
                  src="/pictures/ChatGPT Image Jan 10, 2026, 12_36_19 PM.png"
                  alt="Professional structured wiring installation"
                />
              </ImageWrapper>
              <ImageCaption>
                Professional Structured Wiring Installation - Server rack with meticulous cable management
                and color-coded organization showcasing industry best practices
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/ChatGPT Image Jan 10, 2026, 12_36_29 PM.png')}>
              <ImageWrapper>
                <Image
                  src="/pictures/ChatGPT Image Jan 10, 2026, 12_36_29 PM.png"
                  alt="Advanced cable management system"
                />
              </ImageWrapper>
              <ImageCaption>
                Advanced Cable Management - Detailed view of professional cable routing, labeling,
                and infrastructure organization demonstrating our commitment to excellence
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/IMG_0423.jpg')}>

              <ImageWrapper>
                <Image
                  src="/pictures/IMG_0423.jpg"
                  alt="Telecommunications equipment setup"
                />
              </ImageWrapper>
              <ImageCaption>
                Telecommunications Equipment Setup - Advanced engineering solutions with
                meticulous attention to detail and optimal performance configuration
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/IMG_0426.jpg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/IMG_0426.jpg"
                  alt="Professional cable routing"
                />
              </ImageWrapper>
              <ImageCaption>
                Professional Cable Routing - Expertly organized structured wiring installation
                showcasing clean cable management and proper labeling techniques
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/Resized_20251029_114249.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/Resized_20251029_114249.jpeg"
                  alt="Commercial network deployment"
                />
              </ImageWrapper>
              <ImageCaption>
                Commercial Network Deployment - Enterprise-grade infrastructure installation
                with professional rack mounting and comprehensive cable management
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/Resized_20251029_145255.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/Resized_20251029_145255.jpeg"
                  alt="Data center infrastructure requiring remediation"
                />
              </ImageWrapper>
              <ImageCaption>
                Infrastructure Remediation & Cable Management - Disorganized cabling and improper
                infrastructure? We specialize in data center cleanup, structured cable reorganization,
                and TIA/EIA-568 compliant remediation services
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/20140227_182059_4753085218027.jpg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/20140227_182059_4753085218027.jpg"
                  alt="Headend facility installation"
                />
              </ImageWrapper>
              <ImageCaption>
                Headend Facility Installation - Enterprise-grade telecommunications equipment deployment
                with professional rack mounting and system integration
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/IMG_0404_4751483743184.jpg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/IMG_0404_4751483743184.jpg"
                  alt="Central office infrastructure"
                />
              </ImageWrapper>
              <ImageCaption>
                Central Office Infrastructure - Large-scale telecommunications provider equipment
                installation and configuration
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20140304_174230_4752553409642.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20140304_174230_4752553409642.jpeg"
                  alt="Fiber optic network installation"
                />
              </ImageWrapper>
              <ImageCaption>
                Fiber Optic Network Installation - PON infrastructure deployment with precision
                fiber management and testing
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20140304_174244_4752023508809.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20140304_174244_4752023508809.jpeg"
                  alt="DOCSIS equipment configuration"
                />
              </ImageWrapper>
              <ImageCaption>
                DOCSIS Equipment Configuration - CMTS and RF equipment setup for large-scale
                cable infrastructure deployments
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20140513_141238_4751515119070.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20140513_141238_4751515119070.jpeg"
                  alt="Network infrastructure deployment"
                />
              </ImageWrapper>
              <ImageCaption>
                Network Infrastructure Deployment - Professional installation and turn-up of
                telecommunications provider equipment
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20150415_111743_4751105542559.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20150415_111743_4751105542559.jpeg"
                  alt="Coaxial network auditing"
                />
              </ImageWrapper>
              <ImageCaption>
                Coaxial Network Auditing - Comprehensive testing and validation of coax
                infrastructure for large-scale providers
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20150415_113723_4750721739070.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20150415_113723_4750721739070.jpeg"
                  alt="Equipment rack installation"
                />
              </ImageWrapper>
              <ImageCaption>
                Equipment Rack Installation - Structured wiring and professional cable management
                for telecommunications facilities
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20150422_084357_4750288568914.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20150422_084357_4750288568914.jpeg"
                  alt="RF testing and optimization"
                />
              </ImageWrapper>
              <ImageCaption>
                RF Testing & Optimization - Signal analysis and performance validation for
                cable network infrastructure
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20150422_084704_4749899270581.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20150422_084704_4749899270581.jpeg"
                  alt="XGS-PON deployment"
                />
              </ImageWrapper>
              <ImageCaption>
                XGS-PON Deployment - 10G fiber optic network engineering and OLT platform
                configuration for telecommunications providers
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20150512_145444_4749520500789.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20150512_145444_4749520500789.jpeg"
                  alt="Network testing and certification"
                />
              </ImageWrapper>
              <ImageCaption>
                Network Testing & Certification - Quality assurance and performance benchmarking
                for large-scale deployments
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20150513_085517_4749152872925.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20150513_085517_4749152872925.jpeg"
                  alt="Telecommunications infrastructure"
                />
              </ImageWrapper>
              <ImageCaption>
                Telecommunications Infrastructure - Enterprise-grade structured cabling and
                equipment installation for provider networks
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_20150518_075410_4748757614331.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_20150518_075410_4748757614331.jpeg"
                  alt="Cable management system"
                />
              </ImageWrapper>
              <ImageCaption>
                Cable Management System - Professional structured wiring with meticulous
                organization for telecommunications facilities
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_IMAG0145_4753919306413.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_IMAG0145_4753919306413.jpeg"
                  alt="Headend equipment installation"
                />
              </ImageWrapper>
              <ImageCaption>
                Headend Equipment Installation - Large-scale provider infrastructure with
                professional rack mounting and configuration
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_IMAG0228_4753530050944.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_IMAG0228_4753530050944.jpeg"
                  alt="Network infrastructure engineering"
                />
              </ImageWrapper>
              <ImageCaption>
                Network Infrastructure Engineering - Industrial-scale telecommunications equipment
                deployment and system integration
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/Resized_IMAG0229_4753120631830.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/Resized_IMAG0229_4753120631830.jpeg"
                  alt="Professional cable routing"
                />
              </ImageWrapper>
              <ImageCaption>
                Professional Cable Routing - Expert structured wiring installation with
                TIA/EIA-568 compliant standards
              </ImageCaption>
            </ImageCard>

            <ImageCard onClick={() => openModal('/pictures/january-11-images/image1_4750701801883.jpeg')}>
              <ImageWrapper>
                <Image
                  src="/pictures/january-11-images/image1_4750701801883.jpeg"
                  alt="Central office deployment"
                />
              </ImageWrapper>
              <ImageCaption>
                Central Office Deployment - Comprehensive telecommunications infrastructure
                installation for large-scale providers
              </ImageCaption>
            </ImageCard>
          </ImagesGrid>
        </ImagesSection>

        <Modal isOpen={modalOpen} onClick={closeModal}>
          <CloseButton onClick={closeModal}>×</CloseButton>
          <ModalImage src={selectedImage} alt="Enlarged view" onClick={(e) => e.stopPropagation()} />
        </Modal>
      </Container>
    </GalleryContainer>
  );
};

export default Gallery;
